import { useState, useRef, useEffect, useMemo } from 'react';
import { useGardenMeta } from '../../lib/useGardenMeta';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getGardenSlug, getGardenName } from '../../lib/garden';
import { buildThemeScale } from '../../lib/themeColor';
import { saveWorkerProfile } from '../../lib/faq';
import FaqEntry from '../../components/feature/FaqEntry';

// 🟥 この画面の園名・外観写真・駐車場案内は、以前は全園ぶん箕面の内容を直書きしていた
//    （2026-09-25 発見：多賀城で開いても泉中央で開いても「きらり保育園箕面」の外観・
//    「①②の駐車スペース」がそのまま出ていた。泉中央は敷地内に来訪者用駐車場が無いのに、
//    箕面の駐車場案内を見せていた）。gardens テーブルの実データを取り、
//    無い項目は「その項目ごと出さない」（無いのに箕面のダミーで埋めない）。
const GARDEN_SLUG = getGardenSlug();
const GARDEN_NAME = getGardenName(GARDEN_SLUG);

type CoinParking = {
  name: string;
  address?: string;
  note?: string;
  walk_min?: number;
  lat?: number;
  lng?: number;
};

type GardenRow = {
  id: string;
  name: string;
  bicycle_parking_note: string | null;
  theme_color: string | null;
  photo_exterior_url: string | null;
  photo_parking_url: string | null;
  has_own_parking: boolean | null;
  show_coin_parking: boolean | null;
  parking_note: string | null;
  parking_description: string | null;
  coin_parkings: CoinParking[] | null;
  pickup_time_am: string | null;
  pickup_time_pm: string | null;
  pickup_time_note: string | null;
};

// 🟥 ページ全体を「次へ」で1画面ずつ進むウィザードにした
//    （2026-09-26 後藤さん「全体的に、入力しやすいフォームに再設計したい」）。
//    それまでは読み物と入力欄が1本の長いページに並び、お名前・資格・通勤手段を
//    上の案内と下のフォームで2回聞いていた。1画面に1つの用事だけ置き、
//    車で来ない人には駐車場の画面を出さない。
type StepKey = 'intro' | 'profile' | 'commute' | 'parking' | 'items' | 'manual' | 'mindset' | 'submit';

// 🟥 あおいが最初から最後まで案内する。園の人が「説明係」にならなくていいように。
//    画面ごとに「いま何をすればいいか」を一言だけ言う（長い前置きは書かない）。
const AOI_LINES: Record<StepKey, string> = {
  intro: 'はじめまして。きらり保育園のAI保育士、あおいです。当日までに確認したいことを、順番にご案内します。3分ほどで終わります。',
  profile: 'まずはお名前と、お持ちの資格を教えてください。資格証をタイミーに登録済みなら、それで確認できます。',
  commute: '当日はどうやってお越しですか。選んでいただくと、必要なご案内だけお見せします。',
  parking: 'お車ですね。停める場所と、混み合う時間帯をお伝えします。',
  items: '持ち物はこれだけです。動きやすい服装でお越しください。',
  manual: 'きらりが大切にしていることを、4つだけお話しします。1分ほどです。',
  mindset: '最後にひとつ、きらりの考え方を読んでください。',
  submit: 'ありがとうございます。ご連絡先をいただければ完了です。スポットワークで分からないこと、困ったことがあったら、なんでもわたしに聞いてください。',
};

// マニュアル（4画面）の中でも、あおいが一言ずつ添える
const AOI_MANUAL_LINES: Record<number, string> = {
  1: 'まずは、給食と空気のこと。',
  2: 'きらりが何を目指しているか、です。',
  3: 'ひとつだけ、お願いがあります。',
  4: '最後に、大事なお願いと、わたしからのひとこと。',
};

// 🟥 最終画面の「あおいに聞く」から/chatへ移動すると、Homeは一度アンマウントされる。
//    そこから「戻る」で"/"に戻ると、ここまで入力した内容が全部初期値に戻っていた
//    （2026-09-27 後藤さん「あおいチャットでから戻るボタンで初期化させてしまった」）。
//    ウィザードの入力内容をsessionStorageに退避し、戻ってきたら復元する。
const WIZARD_STORAGE_KEY = `timee_wizard_${GARDEN_SLUG}`;

type SavedWizardState = {
  stepIndex: number;
  formData: { お名前: string; email: string; 通勤手段: string; その他ご質問ご連絡事項: string };
  selectedQualifications: string[];
  submitMethod: '' | 'uploaded' | 'email';
  qualificationName: string;
  itemsConfirmed: boolean;
  onboardingCompleted: {
    給食空気理解: boolean;
    理念方針理解: boolean;
    顧客目線理解: boolean;
    香り理解: boolean;
    キャンセル理解: boolean;
    笑顔理解: boolean;
  };
};

function loadSavedWizardState(): Partial<SavedWizardState> {
  try {
    const raw = sessionStorage.getItem(WIZARD_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function clearSavedWizardState() {
  try {
    sessionStorage.removeItem(WIZARD_STORAGE_KEY);
  } catch {
    // 消せなくても致命的ではない（次回上書きされるだけ）
  }
}

export default function Home() {
  useGardenMeta();
  const navigate = useNavigate();
  const [garden, setGarden] = useState<GardenRow | null>(null);
  // 🟥 どの園の人かを workers.garden_id に残す（2026-10-03 まで記録しておらず、泉の人が箕面の名簿に混ざっていた）
  const gardenId = garden?.id ?? null;
  const restored = useRef(loadSavedWizardState()).current;

  useEffect(() => {
    supabase
      .from('gardens')
      .select(
        'id,name,bicycle_parking_note,theme_color,photo_exterior_url,photo_parking_url,has_own_parking,show_coin_parking,parking_note,parking_description,coin_parkings,pickup_time_am,pickup_time_pm,pickup_time_note'
      )
      .eq('slug', GARDEN_SLUG)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setGarden(data as GardenRow);
      });
  }, []);

  // 読み込み中・DB未設定でも、名前だけは必ず正しく出す
  // （GARDEN_NAME は共通ライブラリの静的な実測フォールバック）
  const displayName = garden?.name ?? GARDEN_NAME;

  // 🟥 園ごとの色（gardens.theme_color）から濃淡パレットを作り、CSS変数として最上位に載せる。
  //    未設定の間は既定のオレンジ（従来の見た目）のまま
  // 🟥 データが届くまでの一瞬、箕面のオレンジ・箕面の外観写真が見えていた
  //    （2026-09-26 後藤さん「アクセス時に一瞬、箕面が見える」）。
  //    読み込み中の既定値を「よその園そのもの」にしない。中立なグレーにする。
  const theme = useMemo(() => buildThemeScale(garden?.theme_color ?? '#9CA3AF'), [garden?.theme_color]);
  const [formData, setFormData] = useState(
    restored.formData ?? {
      お名前: '',
      email: '',
      通勤手段: '',
      その他ご質問ご連絡事項: '',
    }
  );
  const [selectedQualifications, setSelectedQualifications] = useState<string[]>(restored.selectedQualifications ?? []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // 資格証の提出方法（タイミーにアップロード済み／ここで写真を上げる）
  // 🟥 メールで別途提出は廃止（2026-10-03 後藤さん「やり取りが分散しない設計」）。info@ に資格証を送らせない。
  //    保存先は非公開の worker-docs（上げるだけ・読むのはメソッドの管理画面）。key 'email' は過去の保存状態との互換のため残す。
  const [submitMethod, setSubmitMethod] = useState<'' | 'uploaded' | 'email'>(restored.submitMethod ?? '');
  // 🟥 File は state に配列で持つ（input の value を空にすると FileList が消えるため・feedback_file_upload_two_traps）
  const [licenseFiles, setLicenseFiles] = useState<File[]>([]);
  const isAlreadyUploaded = submitMethod === 'uploaded';

  // 資格証送信用の名前入力
  const [qualificationName, setQualificationName] = useState(restored.qualificationName ?? '');

  // 画像拡大モーダル用の状態
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // コインパーキングアコーディオンの状態
  const [isParkingAccordionOpen, setIsParkingAccordionOpen] = useState(false);

  // オンボーディング内画像拡大モーダル用の状態
  const [onboardingImageModal, setOnboardingImageModal] = useState<{
    isOpen: boolean;
    src: string;
    alt: string;
  }>({ isOpen: false, src: '', alt: '' });

  // 持ち物確認チェックボックス
  const [itemsConfirmed, setItemsConfirmed] = useState(restored.itemsConfirmed ?? false);

  // ウィザードの現在位置
  const [stepIndex, setStepIndex] = useState(restored.stepIndex ?? 0);

  // オンボーディング用の状態
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  // オンボーディング理解確認の状態
  const [onboardingCompleted, setOnboardingCompleted] = useState(restored.onboardingCompleted ?? {
    給食空気理解: false,
    理念方針理解: false,
    顧客目線理解: false,
    香り理解: false,
    キャンセル理解: false,
    笑顔理解: false,
  });

  // オンボーディング内画像を開く
  const openOnboardingImage = (src: string, alt: string) => {
    setOnboardingImageModal({ isOpen: true, src, alt });
  };

  // オンボーディング内画像を閉じる
  const closeOnboardingImage = () => {
    setOnboardingImageModal({ isOpen: false, src: '', alt: '' });
  };

  // あおいチャットへ寄り道して戻ってきても続きから、を保つための保存
  useEffect(() => {
    try {
      const toSave: SavedWizardState = {
        stepIndex, formData, selectedQualifications, submitMethod, qualificationName,
        itemsConfirmed, onboardingCompleted,
      };
      sessionStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(toSave));
    } catch {
      // 保存できなくても致命的ではない（次に開いたとき最初から、になるだけ）
    }
  }, [stepIndex, formData, selectedQualifications, submitMethod, qualificationName, itemsConfirmed, onboardingCompleted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const qualificationStatus = isAlreadyUploaded ? 'タイミーにアップロード済み' : 'フォームで写真を提出';

    // 資格証の写真を非公開の置き場へ（上げるだけ）
    const documentPaths: { kind: string; path: string; uploaded_at: string }[] = [];
    if (!isAlreadyUploaded) {
      for (const f of licenseFiles) {
        const ext = (f.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
        const path = `${GARDEN_SLUG}/${new Date().toISOString().slice(0, 10)}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage.from('worker-docs').upload(path, f, { contentType: f.type || undefined, upsert: false });
        if (!upErr) documentPaths.push({ kind: 'license', path, uploaded_at: new Date().toISOString() });
      }
    }

    try {
      // 1. workers テーブルに upsert
      const { data: workerData, error: workerError } = await supabase
        .from('workers')
        .upsert(
          {
            name: formData.お名前,
            email: formData.email,
            qualifications: selectedQualifications.length > 0 ? selectedQualifications : ['未選択'],
            garden_id: gardenId,
            ...(documentPaths.length ? { document_paths: documentPaths } : {}),
          },
          { onConflict: 'email', ignoreDuplicates: false }
        )
        .select('id')
        .single();

      if (workerError) throw workerError;

      // 2. worker_onboardings テーブルに insert
      const { error: onboardingError } = await supabase
        .from('worker_onboardings')
        .insert({
          worker_id: workerData.id,
          commute_method: formData.通勤手段,
          items_confirmed: itemsConfirmed,
          qualification_status: qualificationStatus,
          manual_completed: {
            給食空気理解: onboardingCompleted.給食空気理解,
            理念方針理解: onboardingCompleted.理念方針理解,
            顧客目線理解: onboardingCompleted.顧客目線理解,
            香り理解: onboardingCompleted.香り理解,
            キャンセル理解: onboardingCompleted.キャンセル理解,
            笑顔理解: onboardingCompleted.笑顔理解,
          },
          notes: formData.その他ご質問ご連絡事項 || null,
        });

      if (onboardingError) throw onboardingError;

      // 3. Edge Function でメール送信（失敗してもフォーム送信は成功扱い）
      try {
        await fetch(
          'https://adscvyckqhznzoxyjglc.supabase.co/functions/v1/send-form-email',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              gardenName: GARDEN_NAME,
              gardenSlug: GARDEN_SLUG,
              name: formData.お名前,
              email: formData.email,
              qualifications: selectedQualifications.length > 0 ? selectedQualifications : ['未選択'],
              qualificationStatus,
              commuteMethod: formData.通勤手段,
              itemsConfirmed,
              notes: formData.その他ご質問ご連絡事項 || '',
              manualCompleted: {
                給食空気理解: onboardingCompleted.給食空気理解,
                理念方針理解: onboardingCompleted.理念方針理解,
                顧客目線理解: onboardingCompleted.顧客目線理解,
                香り理解: onboardingCompleted.香り理解,
                キャンセル理解: onboardingCompleted.キャンセル理解,
                笑顔理解: onboardingCompleted.笑顔理解,
              },
            }),
          }
        );
      } catch (emailError) {
        console.error('Email sending failed:', emailError);
      }

      setSubmitStatus('success');
      saveWorkerProfile({ name: formData.お名前, email: formData.email });
      clearSavedWizardState();
      setTimeout(() => navigate('/thanks'), 1500);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // チェックボックス変更ハンドラー
  const handleQualificationChange = (qualification: string) => {
    setSelectedQualifications(prev => {
      if (prev.includes(qualification)) {
        return prev.filter(q => q !== qualification);
      } else {
        return [...prev, qualification];
      }
    });
  };

  // 資格証送信用の名前変更ハンドラー
  const handleQualificationNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setQualificationName(name);
    // フォームの名前欄にも反映
    setFormData(prev => ({
      ...prev,
      お名前: name,
    }));
  };

  // オンボーディング次へ
  const handleNextStep = () => {
    if (currentStep < totalSteps) {
      // 各ステップ完了時に理解状態を更新
      if (currentStep === 1) {
        setOnboardingCompleted(prev => ({ ...prev, 給食空気理解: true }));
      } else if (currentStep === 2) {
        setOnboardingCompleted(prev => ({ ...prev, 理念方針理解: true }));
      } else if (currentStep === 3) {
        setOnboardingCompleted(prev => ({ ...prev, 香り理解: true }));
      }
      setCurrentStep(currentStep + 1);
      // モーダルコンテンツを上部にスクロール
      setTimeout(() => {
        if (onboardingContentRef.current) {
          onboardingContentRef.current.scrollTop = 0;
        }
      }, 0);
    } else {
      // 最終ステップ完了（直前キャンセルと笑顔は同じ画面にまとめてある）
      setOnboardingCompleted(prev => ({ ...prev, キャンセル理解: true, 笑顔理解: true }));
      setShowOnboarding(false);
      setCurrentStep(1);
    }
  };

  // オンボーディングを閉じる
  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
    setCurrentStep(1);
  };

  // オンボーディングコンテンツのref
  const onboardingContentRef = useRef<HTMLDivElement>(null);

  // ---- ウィザードの並び。駐車場は自家用車の人にだけ出す ----
  const steps = useMemo<StepKey[]>(() => {
    const list: StepKey[] = ['intro', 'profile', 'commute'];
    if (formData.通勤手段 === '自家用車') list.push('parking');
    list.push('items', 'manual', 'mindset', 'submit');
    return list;
  }, [formData.通勤手段]);
  const stepKey: StepKey = steps[Math.min(stepIndex, steps.length - 1)];
  // 顧客目線はモーダルの外（専用の画面）にあるので、マニュアル読了の判定には入れない
  const manualDone =
    onboardingCompleted.給食空気理解 &&
    onboardingCompleted.理念方針理解 &&
    onboardingCompleted.香り理解 &&
    onboardingCompleted.キャンセル理解 &&
    onboardingCompleted.笑顔理解;

  // その画面の用事が済んでいるときだけ「次へ」を押せる
  const proceed: { ok: boolean; hint: string } = (() => {
    switch (stepKey) {
      case 'profile': {
        const missing: string[] = [];
        if (qualificationName.trim() === '') missing.push('お名前');
        if (selectedQualifications.length === 0) missing.push('資格');
        if (submitMethod === '') missing.push('資格証の提出方法');
        if (submitMethod === 'email' && licenseFiles.length === 0) missing.push('資格証の写真');
        return { ok: missing.length === 0, hint: missing.length ? `${missing.join('・')}を選ぶと進めます` : '' };
      }
      case 'commute':
        return { ok: formData.通勤手段 !== '', hint: '通勤手段を1つ選ぶと進めます' };
      case 'items':
        return { ok: itemsConfirmed, hint: '「持ち物を確認しました」にチェックすると進めます' };
      case 'manual':
        return { ok: manualDone, hint: 'マニュアルを最後まで読むと進めます' };
      default:
        return { ok: true, hint: '' };
    }
  })();

  const goNext = () => {
    if (!gate.ok) return;
    if (stepKey === 'mindset') {
      setOnboardingCompleted((prev) => ({ ...prev, 顧客目線理解: true }));
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };
  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));
  const jumpTo = (key: StepKey) => {
    const i = steps.indexOf(key);
    if (i >= 0) setStepIndex(i);
  };

  // 画面が切り替わったら先頭から見せる
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [stepIndex]);

  // 🟥 その画面を下まで見てから「次へ」を押せるようにする
  //    （2026-09-26 後藤さん「各画面を下までスクロールし切ってから、次へが押せるように」）。
  //    駐車場・持ち物・当日の心得は、読み飛ばされたら当日そのまま困る内容なので、
  //    「スクロールし切った」を進む条件に含める。画面に収まりきる短い回は最初から押せる。
  const [reachedBottom, setReachedBottom] = useState(false);
  useEffect(() => {
    setReachedBottom(false);
    let done = false;
    let timer = 0;
    let lastHeight = -1;
    const check = () => {
      if (done) return;
      const height = document.documentElement.scrollHeight;
      const atBottom = window.innerHeight + window.scrollY >= height - 24;
      // 🟥 高さが落ち着いてから判定する。写真の読み込み完了を条件にすると、
      //    園の外観写真（カメラ原寸で数MB）が届くまで「次へ」が開かず、
      //    電波の悪い場所では先に進めなくなる（2026-09-26 本番の泉中央で発生）。
      //    見るのは「直前の測定から高さが変わっていないか」だけにする。
      const settled = height === lastHeight;
      lastHeight = height;
      if (atBottom && settled) {
        done = true;
        window.clearInterval(timer);
        setReachedBottom(true);
      }
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    timer = window.setInterval(check, 300);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      window.clearInterval(timer);
    };
    // マニュアルを読み終えた後・通勤手段を選んだ後は画面の中身が増える。
    // 増えた分（確認済みの一覧・駐輪場の案内）も目に入るよう、判定をやり直す
  }, [stepIndex, showOnboarding, formData.通勤手段]);

  // 入力が足りない → 入力を促す。入力は済んでいて下まで見ていない → 読み進めを促す
  const gate: { ok: boolean; hint: string } = !proceed.ok
    ? proceed
    : reachedBottom
      ? { ok: true, hint: '' }
      : { ok: false, hint: '最後までご確認いただくと「次へ」が押せます' };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[var(--theme-50)] via-white to-[var(--theme-50)] pb-32"
      style={theme as React.CSSProperties}
    >
      {/* オンボーディング内画像拡大モーダル */}
      {onboardingImageModal.isOpen && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4 cursor-pointer"
          onClick={closeOnboardingImage}
        >
          <div className="relative max-w-4xl w-full">
            <button
              onClick={closeOnboardingImage}
              className="absolute -top-12 right-0 text-white text-lg font-medium flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <i className="ri-close-line text-2xl"></i>
              閉じる
            </button>
            <img
              src={onboardingImageModal.src}
              alt={onboardingImageModal.alt}
              className="w-full h-auto rounded-lg max-h-[80vh] object-contain"
            />
          </div>
        </div>
      )}

      {/* オンボーディングモーダル */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* プログレスバー */}
            <div className="bg-[var(--theme-50)] px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700">ステップ {currentStep} / {totalSteps}</span>
                <button
                  onClick={handleCloseOnboarding}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-[var(--theme-500)] h-full rounded-full transition-all duration-500"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <img
                  src="/images/aoi.jpg"
                  alt="あおい"
                  className="w-9 h-9 rounded-full object-cover object-top border-2 border-white shadow flex-shrink-0 bg-white"
                />
                <p className="text-base text-gray-700">{AOI_MANUAL_LINES[currentStep]}</p>
              </div>
            </div>

            {/* コンテンツエリア - スクロール可能 */}
            <div ref={onboardingContentRef} className="p-6 md:p-8 flex-1 overflow-y-auto">
              {/* ステップ1: 給食と空気環境 */}
              {currentStep === 1 && (
                <div className="flex flex-col">
                  <div className="flex items-center justify-center mb-5">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-500 rounded-full shadow-lg">
                      <i className="ri-restaurant-line text-3xl text-white"></i>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-5 text-center">給食と空気へのこだわり</h3>

                  <div className="flex items-start gap-4 mb-5">
                    <p className="flex-1 text-base text-gray-700 leading-relaxed">
                      子どもたちの体を作る食事だからこそ、
                      <strong className="text-green-600">「新米食育給食の実施」</strong>や
                      <strong className="text-green-600">「無添加調味料」</strong>を大切にしています。
                    </p>
                    <div
                      className="w-28 h-28 flex-shrink-0 rounded-lg overflow-hidden border-2 border-green-200 shadow-md cursor-pointer"
                      onClick={() => openOnboardingImage('/images/kyushoku.png', '給食の様子')}
                    >
                      <img src="/images/kyushoku.png" alt="給食の様子" className="w-full h-full object-cover object-top" />
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <p className="text-base text-gray-700 leading-relaxed mb-3">
                        壁はウイルスや花粉、カビを不活性化する
                        <strong className="text-green-600">「スペイン本漆喰」</strong>。
                      </p>
                      <div className="bg-pink-50 border-l-4 border-pink-400 rounded-lg p-3">
                        <p className="text-base text-pink-800">
                          <i className="ri-heart-fill text-pink-500 mr-2"></i>
                          この壁、<strong>保育士たち自らの手で塗りました</strong>。
                        </p>
                      </div>
                    </div>
                    <div
                      className="w-28 h-28 flex-shrink-0 rounded-lg overflow-hidden border-2 border-green-200 shadow-md cursor-pointer"
                      onClick={() => openOnboardingImage('/images/kabe.png', 'スペイン本漆喰の壁')}
                    >
                      <img src="/images/kabe.png" alt="スペイン本漆喰の壁" className="w-full h-full object-cover" />
                    </div>
                  </div>
                </div>
              )}

              {/* ステップ2: 理念と方針 */}
              {currentStep === 2 && (
                <div className="flex flex-col">
                  <div className="flex items-center justify-center mb-5">
                    <div className="w-16 h-16 flex items-center justify-center bg-[var(--theme-500)] rounded-full shadow-lg">
                      <i className="ri-star-smile-line text-3xl text-white"></i>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-5 text-center">
                    子どもも、あなたも。<br />「人生の主役」になれる場所
                  </h3>

                  <div className="bg-[var(--theme-50)] border-2 border-[var(--theme-200)] rounded-2xl p-5 mb-5">
                    <p className="text-sm font-bold text-[var(--theme-700)] mb-1">保育理念</p>
                    <p className="text-xl font-bold text-gray-800 mb-3">『おほしさま』をつくる</p>
                    <p className="text-base text-gray-700 leading-relaxed">
                      0〜2歳は人格形成の土台となる、一生に一度のかけがえのない時期。子どもたちが将来、自分の個性に自信を持って輝く「人生の主役」になれるように。
                    </p>
                  </div>

                  <p className="text-sm font-bold text-gray-500 mb-2">保育方針 ― 未来へつなぐ3つのチカラ</p>
                  <div className="space-y-2 mb-5">
                    {(
                      [
                        ['Challenge!', '失敗を恐れず、自分の力で壁を乗り越える強さを。'],
                        ['Respect!', '自分の気持ちに素直に、お互いの個性を認め合う優しさを。'],
                        ['Be Yourself!', '自分の選択に誇りを持ち、「自分の人生」を生きる自信を。'],
                      ] as const
                    ).map(([title, body], i) => (
                      <div key={title} className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4">
                        <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-full text-sm font-bold">
                          {i + 1}
                        </span>
                        <div>
                          <p className="text-base font-bold text-[var(--theme-700)]">{title}</p>
                          <p className="text-base text-gray-700">{body}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-pink-50 border-l-4 border-pink-400 rounded-lg p-4">
                    <p className="text-base text-pink-900">
                      大人が嘘なく、心からの笑顔でいること。それが最高の子育て環境になります。
                    </p>
                  </div>
                </div>
              )}

              {/* ステップ3: 香りのお願い（理由もここにまとめる） */}
              {currentStep === 3 && (
                <div className="flex flex-col">
                  <div className="flex items-center justify-center mb-5">
                    <div className="w-16 h-16 flex items-center justify-center bg-red-500 rounded-full shadow-lg">
                      <i className="ri-alert-line text-3xl text-white"></i>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2 text-center">
                    柔軟剤・洗剤の香りは<br />控えてください
                  </h3>
                  <p className="text-base text-gray-600 text-center mb-5">
                    香害や化学物質過敏症を気にするご家庭が増えています。
                  </p>

                  <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-5 mb-5">
                    <p className="text-base font-bold text-red-900 mb-3">なぜ控えていただくのか</p>
                    <ul className="space-y-2 text-base text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-blank-circle-fill text-[8px] text-red-400 mt-2"></i>
                        <span>子どもは呼吸量が大人の約2倍。皮膚も薄く、香料を取り込みやすい</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-blank-circle-fill text-[8px] text-red-400 mt-2"></i>
                        <span>強い香りで、頭痛・咳・集中力低下・情緒不安定が起きることがある</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-blank-circle-fill text-[8px] text-red-400 mt-2"></i>
                        <span>柔軟剤のマイクロカプセルはプラスチックの粒。衣類から落ちて吸い込む心配も</span>
                      </li>
                    </ul>
                  </div>

                  <div className="flex items-center gap-4 bg-[var(--theme-50)] border border-[var(--theme-200)] rounded-xl p-4">
                    <div className="flex-1">
                      <p className="text-base text-gray-700">
                        だからきらり保育園は、
                        <strong className="text-[var(--theme-700)]">完全無香料・無添加の洗浄剤</strong>
                        を自分たちで作りました。
                      </p>
                    </div>
                    <div
                      className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden border-2 border-[var(--theme-200)] shadow-md cursor-pointer bg-white"
                      onClick={() => openOnboardingImage('/images/senzai.png', 'きらりの無添加洗浄剤')}
                    >
                      <img src="/images/senzai.png" alt="きらりの無添加洗浄剤" className="w-full h-full object-contain" />
                    </div>
                  </div>
                </div>
              )}

              {/* ステップ4: 直前キャンセルと、最後のお願い */}
              {currentStep === 4 && (
                <div className="flex flex-col">
                  <div className="w-full h-36 mb-5 rounded-2xl overflow-hidden bg-gray-100">
                    <img src="/images/kodomotachi.png" alt="きらり保育園の子どもたち" className="w-full h-full object-cover" />
                  </div>

                  <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-5">
                    <h4 className="text-lg font-bold text-red-900 mb-2 flex items-center gap-2">
                      <i className="ri-calendar-close-line"></i>
                      直前のキャンセルは避けてください
                    </h4>
                    <p className="text-base text-gray-700">
                      保育現場では<strong className="text-red-700">安全面への影響が非常に大きい</strong>ため、
                      連続した場合は今後の勤務をご遠慮いただいています。
                    </p>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-800 mb-3 text-center">あなたの笑顔が必要です</h3>
                  <p className="text-base text-gray-700 leading-relaxed text-center mb-5">
                    分からないことがあったら、どんどん聞いてください。<br />
                    そして、笑顔で子ども達と過ごしてください。
                  </p>

                  <div className="bg-[var(--theme-50)] border-2 border-[var(--theme-200)] rounded-2xl p-5 text-center">
                    <p className="text-sm font-bold text-[var(--theme-700)] mb-1">きらり保育園理念</p>
                    <p className="text-xl font-bold text-gray-800 mb-2">心から輝く人をつくる</p>
                    <p className="text-base text-gray-700">
                      だからこそ、大人であるあなたも輝いていて欲しい。<br />そんな想いで運営しています。
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* ボタンエリア */}
            <div className="px-8 pb-8 flex-shrink-0">
              <button
                onClick={handleNextStep}
                className="w-full bg-[var(--theme-500)] text-white font-bold py-4 px-6 rounded-xl hover:bg-[var(--theme-600)] transition-all duration-200 shadow-lg hover:shadow-xl text-lg whitespace-nowrap cursor-pointer"
              >
                {currentStep < totalSteps ? (
                  <span className="flex items-center justify-center gap-2">
                    理解しました。次へ
                    <i className="ri-arrow-right-line text-xl"></i>
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-checkbox-circle-line text-xl"></i>
                    完了
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 画像拡大モーダル */}
      {isImageModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setIsImageModalOpen(false)}
              className="absolute -top-12 right-0 text-white text-lg font-medium flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <i className="ri-close-line text-2xl"></i>
              閉じる
            </button>
            {garden?.photo_parking_url ? (
              <img
                src={garden.photo_parking_url}
                alt="駐車場案内"
                className="w-full h-auto rounded-lg"
              />
            ) : (
              <div className="w-full aspect-video rounded-lg bg-white/10 animate-pulse" />
            )}
          </div>
        </div>
      )}

      {/* ウィザード進行バー */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-gray-700 truncate">{displayName}</span>
            <span className="text-gray-500 whitespace-nowrap">
              {stepIndex + 1} / {steps.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1.5 overflow-hidden">
            <div
              className="bg-[var(--theme-500)] h-full rounded-full transition-all duration-500"
              style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* あおいの案内 */}
      <div className="max-w-5xl mx-auto px-4 pt-4 pb-1">
        <div className="flex items-start gap-3">
          <img
            src="/images/aoi.jpg"
            alt="あおい"
            className="w-12 h-12 rounded-full object-cover object-top border-2 border-white shadow-md flex-shrink-0 bg-white"
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-gray-500 mb-1">きらり保育園　あおい</p>
            <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
              <p className="text-base text-gray-800 leading-relaxed">{AOI_LINES[stepKey]}</p>
            </div>
          </div>
        </div>
      </div>

      {stepKey === 'intro' && (
      <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[var(--theme-400)] to-[var(--theme-500)] text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-6 py-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            ワーカー様<br />
            受入マニュアル
          </h1>
          <p className="text-xl md:text-2xl mb-4 font-medium">{displayName}</p>
        </div>
      </section>

      {/* 外観画像セクション */}
      <section className="max-w-5xl mx-auto px-6 py-6">
        {/* 写真が届くまで高さを確保し、読み込みでレイアウトが跳ねないようにする */}
        <div className="rounded-2xl overflow-hidden shadow-xl aspect-video bg-gray-100">
          {garden?.photo_exterior_url ? (
            <img
              src={garden.photo_exterior_url}
              alt={`${displayName} 外観`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full aspect-video bg-gray-100 animate-pulse" />
          )}
        </div>
      </section>

      {/* 概要セクション */}
      <section className="max-w-5xl mx-auto px-6 py-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 border border-[var(--theme-100)]">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-full flex-shrink-0">
              <i className="ri-information-line text-2xl"></i>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                【重要】初回勤務時の事前確認について
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {displayName}での勤務にご応募いただきありがとうございます。
                <br />
                当日、皆様に気持ちよく勤務していただくため、「次へ」を押しながら順番にご確認・ご入力ください。
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 md:ml-16">
            {['お名前と資格', '通勤手段', '持ち物・服装', 'マニュアル', '連絡先'].map((t, i) => (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 bg-[var(--theme-50)] border border-[var(--theme-200)] text-[var(--theme-800)] text-sm font-medium px-3 py-1.5 rounded-full"
              >
                <span className="w-5 h-5 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-full text-xs">{i + 1}</span>
                {t}
              </span>
            ))}
          </div>
          <p className="mt-4 text-base text-gray-500 flex items-center gap-2 md:ml-16">
            <i className="ri-time-line"></i>
            所要時間は3分ほどです
          </p>
        </div>
      </section>
      </>
      )}

      {/* 資格確認セクション */}
      {stepKey === 'profile' && (
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border-l-4 border-[var(--theme-500)]">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-full flex-shrink-0">
              <i className="ri-file-text-line text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-800">
              お名前と資格
            </h3>
          </div>
          
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            資格確認のため、3つだけ教えてください。
          </p>

          {/* 名前入力欄 */}
          <div className="mb-4">
            <label htmlFor="qualificationName" className="block text-base font-semibold text-gray-700 mb-2">
              お名前 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="qualificationName"
              value={qualificationName}
              onChange={handleQualificationNameChange}
              autoComplete="name"
              className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[var(--theme-500)] focus:border-transparent text-lg"
              placeholder="山田 太郎"
            />
          </div>

          {/* 資格選択チェックボックス */}
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">保有資格を選択してください <span className="text-red-500">*</span></p>
            <div className="flex flex-wrap gap-3">
              {(['保育士', '看護師'] as const).map((q) => {
                const on = selectedQualifications.includes(q);
                return (
                  <label
                    key={q}
                    className={`flex-1 min-w-[140px] flex items-center gap-3 cursor-pointer border-2 rounded-xl px-4 py-4 transition-all active:scale-[0.98] ${
                      on ? 'bg-[var(--theme-50)] border-[var(--theme-500)]' : 'bg-white border-gray-200 hover:border-[var(--theme-300)]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => handleQualificationChange(q)}
                      className="w-6 h-6 text-[var(--theme-500)] border-gray-300 rounded focus:ring-[var(--theme-500)] cursor-pointer"
                    />
                    <span className="text-lg text-gray-800 font-semibold">{q}</span>
                  </label>
                );
              })}
            </div>
            <p className="mt-2 text-sm text-gray-500">両方お持ちの方は両方選んでください</p>
          </div>

          {/* 資格証の提出方法選択 */}
          <div className="border-t border-gray-200 pt-6">
            <p className="text-base font-semibold text-gray-800 mb-4">
              <i className="ri-file-upload-line text-[var(--theme-500)] mr-2"></i>
              資格証の提出方法を選択してください
            </p>

            <div className="space-y-3">
              {(
                [
                  {
                    key: 'uploaded',
                    icon: 'ri-checkbox-circle-fill',
                    title: 'タイミーにアップロード済み',
                    body: 'タイミーのプロフィールに資格証を載せている方はこちら。追加の提出は不要です。',
                  },
                  {
                    key: 'email',
                    icon: 'ri-mail-send-line',
                    title: 'まだ載せていない（ここで写真を上げる）',
                    body: '資格証の写真を、この下から上げてください。メールで送る必要はありません。',
                  },
                ] as const
              ).map((m) => {
                const on = submitMethod === m.key;
                return (
                  <label
                    key={m.key}
                    className={`flex items-start gap-4 cursor-pointer border-2 rounded-xl p-4 transition-all active:scale-[0.98] ${
                      on ? 'bg-[var(--theme-50)] border-[var(--theme-500)]' : 'bg-white border-gray-200 hover:border-[var(--theme-300)]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="qualificationSubmitMethod"
                      checked={on}
                      onChange={() => setSubmitMethod(m.key)}
                      className="w-6 h-6 mt-0.5 text-[var(--theme-500)] border-gray-300 focus:ring-[var(--theme-500)] cursor-pointer"
                    />
                    <div className="flex-1">
                      <span className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <i className={`${m.icon} text-[var(--theme-500)]`}></i>
                        {m.title}
                      </span>
                      <p className="text-base text-gray-600 mt-1">{m.body}</p>
                    </div>
                  </label>
                );
              })}
            </div>
            {submitMethod === 'email' && (
              <div className="mt-4 bg-[var(--theme-50)] border-2 border-dashed border-[var(--theme-300)] rounded-xl p-4">
                <label className="block text-base font-bold text-gray-800 mb-2">資格証の写真（3枚まで）</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={(e) => {
                    const picked = Array.from(e.target.files ?? []);
                    setLicenseFiles((prev) => [...prev, ...picked].slice(0, 3));
                    e.target.value = '';
                  }}
                  className="block w-full text-base text-gray-700"
                />
                {licenseFiles.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {licenseFiles.map((f, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 text-sm text-gray-700 bg-white rounded-lg px-3 py-2">
                        <span className="truncate">{f.name}</span>
                        <button type="button" onClick={() => setLicenseFiles((prev) => prev.filter((_, j) => j !== i))} className="text-gray-400 underline flex-shrink-0">外す</button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-sm text-gray-500">写真は園の担当者だけが見ます。</p>
              </div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* 通勤手段セクション */}
      {stepKey === 'commute' && (
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border-l-4 border-[var(--theme-500)]">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-full flex-shrink-0">
              <i className="ri-car-line text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-800">当日の通勤手段</h3>
          </div>
          
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            当日はどうやって来ますか？ 1つ選んでください。
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
              onClick={() => setFormData({ ...formData, 通勤手段: '自家用車' })}
              className={`flex-1 flex flex-col items-center justify-center gap-3 border-2 rounded-xl p-5 text-center transition-all cursor-pointer active:scale-[0.98] ${
                formData.通勤手段 === '自家用車'
                  ? 'bg-[var(--theme-500)] border-[var(--theme-500)] text-white'
                  : 'bg-gradient-to-br from-[var(--theme-50)] to-[var(--theme-100)] border-[var(--theme-200)]'
              }`}
            >
              <div
                className={`w-12 h-12 flex items-center justify-center rounded-full border-2 ${
                  formData.通勤手段 === '自家用車' ? 'bg-white/20' : 'bg-white'
                }`}
              >
                <i
                  className={`ri-car-fill text-2xl ${
                    formData.通勤手段 === '自家用車' ? 'text-white' : 'text-[var(--theme-500)]'
                  }`}
                ></i>
              </div>
              <p className="font-semibold">自家用車</p>
            </div>
            <div
              onClick={() => setFormData({ ...formData, 通勤手段: 'バイク' })}
              className={`flex-1 flex flex-col items-center justify-center gap-3 border-2 rounded-xl p-5 text-center transition-all cursor-pointer active:scale-[0.98] ${
                formData.通勤手段 === 'バイク'
                  ? 'bg-[var(--theme-500)] border-[var(--theme-500)] text-white'
                  : 'bg-gradient-to-br from-[var(--theme-50)] to-[var(--theme-100)] border-[var(--theme-200)]'
              }`}
            >
              <div
                className={`w-12 h-12 flex items-center justify-center rounded-full border-2 ${
                  formData.通勤手段 === 'バイク' ? 'bg-white/20' : 'bg-white'
                }`}
              >
                <i
                  className={`ri-motorbike-fill text-2xl ${
                    formData.通勤手段 === 'バイク' ? 'text-white' : 'text-[var(--theme-500)]'
                  }`}
                ></i>
              </div>
              <p className="font-semibold">バイク</p>
            </div>
            <div
              onClick={() => setFormData({ ...formData, 通勤手段: '自転車' })}
              className={`flex-1 flex flex-col items-center justify-center gap-3 border-2 rounded-xl p-5 text-center transition-all cursor-pointer active:scale-[0.98] ${
                formData.通勤手段 === '自転車'
                  ? 'bg-[var(--theme-500)] border-[var(--theme-500)] text-white'
                  : 'bg-gradient-to-br from-[var(--theme-50)] to-[var(--theme-100)] border-[var(--theme-200)]'
              }`}
            >
              <div
                className={`w-12 h-12 flex items-center justify-center rounded-full border-2 ${
                  formData.通勤手段 === '自転車' ? 'bg-white/20' : 'bg-white'
                }`}
              >
                <i
                  className={`ri-bike-fill text-2xl ${
                    formData.通勤手段 === '自転車' ? 'text-white' : 'text-[var(--theme-500)]'
                  }`}
                ></i>
              </div>
              <p className="font-semibold">自転車</p>
            </div>
            <div
              onClick={() => setFormData({ ...formData, 通勤手段: '徒歩' })}
              className={`flex-1 flex flex-col items-center justify-center gap-3 border-2 rounded-xl p-5 text-center transition-all cursor-pointer active:scale-[0.98] ${
                formData.通勤手段 === '徒歩'
                  ? 'bg-[var(--theme-500)] border-[var(--theme-500)] text-white'
                  : 'bg-gradient-to-br from-[var(--theme-50)] to-[var(--theme-100)] border-[var(--theme-200)]'
              }`}
            >
              <div
                className={`w-12 h-12 flex items-center justify-center rounded-full border-2 ${
                  formData.通勤手段 === '徒歩' ? 'bg-white/20' : 'bg-white'
                }`}
              >
                <i
                  className={`ri-walk-fill text-2xl ${
                    formData.通勤手段 === '徒歩' ? 'text-white' : 'text-[var(--theme-500)]'
                  }`}
                ></i>
              </div>
              <p className="font-semibold">公共交通機関と徒歩</p>
            </div>
          </div>
          
          {formData.通勤手段 && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-green-500"></i>
              <span className="text-green-800 font-medium">
                「{formData.通勤手段 === '徒歩' ? '公共交通機関と徒歩' : formData.通勤手段}」を選択しました
              </span>
            </div>
          )}

          {/* バイク・自転車の方への駐輪場案内（園ごとの gardens.bicycle_parking_note。無い園には出さない） */}
          {garden?.bicycle_parking_note && (formData.通勤手段 === 'バイク' || formData.通勤手段 === '自転車') && (
          <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-blue-500 text-white rounded-full text-sm font-bold flex-shrink-0">
              <i className="ri-parking-line text-xl"></i>
            </div>
            <div>
              <h4 className="text-lg font-bold text-blue-900 mb-1 flex items-center gap-2">
                <i className="ri-motorbike-line"></i>
                <i className="ri-bike-line"></i>
                バイク・自転車の方へ
              </h4>
              <p className="text-base text-gray-700">{garden.bicycle_parking_note}</p>
            </div>
          </div>
          )}
        </div>
      </section>
      )}

      {/* 駐車場案内セクション（自家用車の人にだけ出る） */}
      {stepKey === 'parking' && (
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border-l-4 border-[var(--theme-500)]">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center bg-[var(--theme-100)] text-[var(--theme-600)] rounded-full flex-shrink-0">
              <i className="ri-parking-box-line text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-800">駐車場のご案内</h3>
          </div>

          {garden?.has_own_parking ? (
            <div className="mb-6">
              <h4 className="text-xl font-bold text-blue-900 mb-3 flex items-center gap-2">
                <i className="ri-car-fill text-blue-500"></i>
                自家用車でお越しの方へ
              </h4>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                {garden.parking_description ?? '園の駐車場をご利用ください。'}
              </p>

              {garden.photo_parking_url && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                    <i className="ri-image-line"></i>
                    駐車場の様子（タップで拡大）
                  </p>
                  <div
                    onClick={() => setIsImageModalOpen(true)}
                    className="cursor-pointer rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[var(--theme-400)] transition-colors shadow-md hover:shadow-lg w-full max-w-xs"
                  >
                    <div className="w-full aspect-[4/3] bg-gray-100">
                      <img
                        src={garden.photo_parking_url}
                        alt="駐車場案内 - タップで拡大"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="bg-gray-100 py-2 px-3 flex items-center justify-center gap-2 text-sm text-gray-600">
                      <i className="ri-zoom-in-line"></i>
                      タップで拡大表示
                    </div>
                  </div>
                </div>
              )}

              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayName)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
              >
                <i className="ri-external-link-line"></i>
                Googleマップで開く
              </a>

              {(garden.pickup_time_am || garden.pickup_time_pm) && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mt-6">
                  <h4 className="text-lg font-bold text-red-900 mb-2 flex items-center gap-2">
                    <i className="ri-alarm-warning-line"></i>
                    【注意】園児の送迎時間帯について
                  </h4>
                  <div className="space-y-2 mb-3">
                    {garden.pickup_time_am && (
                      <p className="text-base text-gray-700 font-semibold">
                        <i className="ri-time-line mr-2 text-red-500"></i>
                        AM {garden.pickup_time_am}
                      </p>
                    )}
                    {garden.pickup_time_pm && (
                      <p className="text-base text-gray-700 font-semibold">
                        <i className="ri-time-line mr-2 text-red-500"></i>
                        PM {garden.pickup_time_pm}
                      </p>
                    )}
                  </div>
                  {garden.pickup_time_note && (
                    <p className="text-base text-gray-700 leading-relaxed">
                      <strong className="text-red-600">{garden.pickup_time_note}</strong>
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : garden ? (
            <div className="mb-6 bg-[var(--theme-50)] border-l-4 border-[var(--theme-500)] rounded-lg p-4">
              <h4 className="text-lg font-bold text-[var(--theme-900)] mb-2 flex items-center gap-2">
                <i className="ri-alert-line"></i>
                園に来訪者用の駐車場はありません
              </h4>
              <p className="text-base text-gray-700 leading-relaxed">
                {garden.parking_note ?? '近くのコインパーキングをご利用ください。'}
              </p>
            </div>
          ) : null}

          {garden?.show_coin_parking && garden.coin_parkings && garden.coin_parkings.length > 0 && (
            <div>
              <h4 className="text-xl font-bold text-yellow-900 mb-4 flex items-center gap-2">
                <i className="ri-parking-line text-yellow-500"></i>
                周辺のコインパーキング情報
              </h4>

              <button
                onClick={() => setIsParkingAccordionOpen(!isParkingAccordionOpen)}
                className="w-full flex items-center justify-between bg-yellow-100 hover:bg-yellow-200 border-2 border-yellow-300 rounded-xl p-4 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3 text-lg font-semibold text-yellow-900">
                  <i className="ri-map-pin-line"></i>
                  コインパーキング一覧（{garden.coin_parkings.length}件）
                </span>
                <i className={`ri-arrow-down-s-line text-2xl text-yellow-600 transition-transform duration-300 ${isParkingAccordionOpen ? 'rotate-180' : ''}`}></i>
              </button>

              <div className={`overflow-hidden transition-all duration-300 ${isParkingAccordionOpen ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                <div className="space-y-3">
                  {garden.coin_parkings.map((cp, i) => (
                    <div key={cp.name + i} className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-yellow-500 text-white rounded-full text-sm font-bold">
                          {i + 1}
                        </div>
                        <div className="flex-1">
                          <h5 className="font-bold text-gray-800 mb-1">{cp.name}</h5>
                          {cp.address && (
                            <p className="text-sm text-gray-600 mb-1">
                              <i className="ri-map-pin-line mr-1"></i>
                              {cp.address}
                            </p>
                          )}
                          {typeof cp.walk_min === 'number' && (
                            <p className="text-sm text-gray-600 mb-2">
                              <i className="ri-walk-line mr-1"></i>
                              徒歩約{cp.walk_min}分
                            </p>
                          )}
                          {cp.note && (
                            <p className="text-sm text-[var(--theme-600)] mb-2">
                              <i className="ri-alert-line mr-1"></i>
                              {cp.note}
                            </p>
                          )}
                          <a
                            // 住所検索だと号地レベルでズレることがある（泉中央パーキングで実測約250m）ので、
                            // 緯度経度があればそちらを優先する
                            href={
                              cp.lat != null && cp.lng != null
                                ? `https://www.google.com/maps/search/?api=1&query=${cp.lat},${cp.lng}`
                                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cp.address ?? cp.name)}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm mt-1"
                          >
                            <i className="ri-map-2-line"></i>
                            地図で見る
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      )}

      {/* 持ち物・服装セクション */}
      {stepKey === 'items' && (
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-full flex-shrink-0">
              <i className="ri-shopping-bag-line text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-800">当日の持ち物・服装</h3>
          </div>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            保育に適した、<strong className="text-[var(--theme-600)]">動きやすい服装</strong>での勤務をお願いします。
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-[var(--theme-50)] rounded-xl p-5 border border-[var(--theme-200)]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-lg">
                  <i className="ri-t-shirt-line text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-800">動きやすいパンツ</h4>
              </div>
              <p className="text-base text-gray-700">
                紺色や黒色のズボン（ジャージ、チノパン等）をご用意ください。
              </p>
            </div>

            <div className="bg-[var(--theme-50)] rounded-xl p-5 border border-[var(--theme-200)]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-lg">
                  <i className="ri-shirt-line text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-800">動きやすいトップス</h4>
              </div>
              <p className="text-base text-gray-700">
                トレーナーやTシャツなど。
                <strong className="text-red-600">フードや紐がついているものは安全上避けてください。</strong>
              </p>
            </div>

            <div className="bg-[var(--theme-50)] rounded-xl p-5 border border-[var(--theme-200)]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-lg">
                  <i className="ri-user-smile-line text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-800">エプロン</h4>
              </div>
              <p className="text-base text-gray-700">
                多少の柄が入っていても問題ありません。
              </p>
            </div>
          </div>

          {/* 持ち物確認チェックボックス */}
          <label className={`flex items-start gap-4 cursor-pointer border-2 rounded-xl p-5 transition-all active:scale-[0.98] ${itemsConfirmed ? 'bg-green-50 border-green-400' : 'bg-white border-gray-200 hover:border-green-300'}`}>
            <input
              type="checkbox"
              checked={itemsConfirmed}
              onChange={(e) => setItemsConfirmed(e.target.checked)}
              className="w-6 h-6 mt-1 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer flex-shrink-0"
            />
            <span className="text-lg text-gray-800">
              <strong className="text-green-700">持ち物を確認しました</strong>
            </span>
          </label>
          {itemsConfirmed && (
            <div className="mt-4 bg-green-100 border border-green-300 rounded-lg p-4 flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
              <p className="text-green-800 font-medium">
                ありがとうございます。当日の準備をお願いいたします。
              </p>
            </div>
          )}
        </div>
      </section>
      )}

      {/* オンボーディング・マニュアルセクション */}
      {stepKey === 'manual' && (
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-[var(--theme-50)] rounded-2xl shadow-lg p-6 md:p-8 border-2 border-[var(--theme-200)]">
          {/* 画像を上部に配置（モバイル表示用） */}
          <div className="w-full h-40 md:hidden mb-6 rounded-2xl overflow-hidden border-2 border-[var(--theme-300)] shadow-lg">
            <img
              src="/images/manual.png"
              alt="きらり保育園マニュアル"
              className="w-full h-full object-contain"
            />
          </div>

          <div className="flex items-start gap-6 mb-6">
            <div className="flex-1">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-full flex-shrink-0">
                  <i className="ri-book-open-line text-2xl"></i>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-gray-800">
                      ワーカーさん<br />マニュアル
                    </h3>
                    <span className="inline-flex items-center gap-1 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                      <i className="ri-alert-line"></i>
                      必読
                    </span>
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    きらり保育園のこだわりや大切にしていることをご紹介します。<br />
                    必ず読んでください。
                  </p>
                </div>
              </div>
            </div>
            
            {/* 右サイド画像（デスクトップ表示用） */}
            <div className="hidden md:block w-48 h-48 flex-shrink-0 rounded-2xl overflow-hidden border-2 border-[var(--theme-300)] shadow-lg">
              <img
                src="/images/manual.png"
                alt="きらり保育園マニュアル"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* オンボーディング理解確認 - マニュアルセクション内に表示 */}
          {(onboardingCompleted.給食空気理解 || onboardingCompleted.理念方針理解 || onboardingCompleted.顧客目線理解 || onboardingCompleted.香り理解 || onboardingCompleted.キャンセル理解 || onboardingCompleted.笑顔理解) && (
            <div className="mb-6">
              <div className="bg-green-50 border-2 border-green-300 rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                  <h4 className="text-lg font-bold text-green-800">マニュアル確認完了</h4>
                </div>
                {onboardingCompleted.給食空気理解 && (
                  <div className="flex items-center gap-3 bg-white rounded-lg p-4">
                    <i className="ri-checkbox-circle-fill text-xl text-green-500"></i>
                    <span className="text-base text-gray-700 font-medium">給食、空気について理解しました</span>
                  </div>
                )}
                {onboardingCompleted.理念方針理解 && (
                  <div className="flex items-center gap-3 bg-white rounded-lg p-4">
                    <i className="ri-checkbox-circle-fill text-xl text-green-500"></i>
                    <span className="text-base text-gray-700 font-medium">理念・方針について理解しました</span>
                  </div>
                )}
                {onboardingCompleted.香り理解 && (
                  <div className="flex items-center gap-3 bg-white rounded-lg p-4">
                    <i className="ri-checkbox-circle-fill text-xl text-green-500"></i>
                    <span className="text-base text-gray-700 font-medium">香りのお願いについて理解しました</span>
                  </div>
                )}
                {onboardingCompleted.キャンセル理解 && (
                  <div className="flex items-center gap-3 bg-white rounded-lg p-4">
                    <i className="ri-checkbox-circle-fill text-xl text-green-500"></i>
                    <span className="text-base text-gray-700 font-medium">直前キャンセルについて理解しました</span>
                  </div>
                )}
                {onboardingCompleted.笑顔理解 && (
                  <div className="flex items-center gap-3 bg-white rounded-lg p-4">
                    <i className="ri-checkbox-circle-fill text-xl text-green-500"></i>
                    <span className="text-base text-gray-700 font-medium">笑顔が大切なこと理解しました</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            onClick={() => setShowOnboarding(true)}
            className="w-full bg-[var(--theme-500)] text-white font-bold py-4 px-6 rounded-xl hover:bg-[var(--theme-600)] transition-all duration-200 shadow-lg hover:shadow-xl text-lg whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center justify-center gap-3">
              <i className={`${manualDone ? 'ri-refresh-line' : 'ri-play-circle-line'} text-2xl`}></i>
              {manualDone ? 'もう一度聞く' : 'あおいの話を聞く'}
            </span>
          </button>

          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600">
            <i className="ri-time-line"></i>
            <span>所要時間：約60秒</span>
          </div>
        </div>
      </section>
      )}

      {/* 当日のご案内セクション */}
      {stepKey === 'mindset' && (
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-br from-[var(--theme-500)] to-[var(--theme-600)] text-white rounded-2xl shadow-lg p-6 md:p-8">
          <div className="flex justify-center mb-6">
            <img
              src="/images/kodomotachi.png"
              alt="きらり保育園の子どもたち"
              className="h-24 w-auto object-contain bg-white rounded-lg px-4 py-2 shadow-lg"
            />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
            きらりは顧客目線
          </h3>
          
          <div className="space-y-6 text-base text-gray-700 leading-relaxed">
            {/* 基準はひとつ */}
            <div className="bg-gradient-to-br from-[var(--theme-50)] to-pink-50 border-2 border-[var(--theme-200)] rounded-2xl p-6">
              <p className="text-xl font-bold text-[var(--theme-600)] mb-3 text-center">
                基準はひとつ。<br />
                「あなたの働く保育園に預けようと思いますか？」
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                保育園が「選ばれる時代」になった今、私たちが大切にすべき顧客目線。それは難しいビジネス用語ではありません。たった一つの問いかけです。
              </p>
              <div className="bg-white rounded-xl p-4 border-2 border-[var(--theme-300)] mt-4">
                <p className="text-base font-bold text-[var(--theme-700)] text-center">
                  「もし自分が親だったら、今の自分のような保育士に、大切な我が子を預けたいと思えますか？」
                </p>
              </div>
            </div>

            {/* 1. 鏡の中の自分を見て */}
            <div className="bg-white border-2 border-indigo-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 flex items-center justify-center bg-indigo-500 text-white rounded-full">
                  <i className="ri-user-search-line text-lg"></i>
                </div>
                <h4 className="text-xl font-bold text-indigo-900">鏡の中の自分を見て、「YES」と言えますか？</h4>
              </div>
              <p className="text-base text-gray-700 mb-4">
                保護者は、設備や建物以上に<strong className="text-indigo-600">「先生の人柄」</strong>を見ています。
              </p>
              <div className="space-y-3">
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="font-bold text-indigo-700 mb-2">その「表情」で預けられますか？</p>
                  <p className="text-sm text-gray-700">
                    あくびをしている、無表情でスマホを見ている、歩き方がダルそう…。そんな先生がいる教室に、泣いている我が子を置いていけますか？
                  </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="font-bold text-indigo-700 mb-2">その「匂い」で抱っこさせられますか？</p>
                  <p className="text-sm text-gray-700">
                    強い香水の匂い、あるいは昨日の食事（キムチやニンニク）の匂いがする服。その腕に、生まれたばかりの赤ちゃんを抱かせたいと思いますか？
                  </p>
                </div>
              </div>
            </div>

            {/* 2. 不快を感じた瞬間 */}
            <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-5">
              <h4 className="text-lg font-bold text-red-800 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full text-sm">2</span>
                「不快」を感じた瞬間、親は離れていく
              </h4>
              <p className="text-base text-gray-700 mb-3">
                あなたが「これくらいバレないだろう」と思った小さな油断。もしあなたが親なら、その違和感を敏感に感じ取るはずです。「なんかこの先生、信用できないな」と。
              </p>
              <div className="bg-white rounded-lg p-4 border border-red-200">
                <p className="text-base font-medium text-red-700">
                  保護者が求めているのは、完璧な技術よりも<strong>「この人なら安心できる」という人間性</strong>です。
                </p>
              </div>
            </div>

            {/* 3. 選ばれる保育士になろう */}
            <div className="bg-gradient-to-br from-yellow-50 to-[var(--theme-50)] border-2 border-yellow-300 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-yellow-500 text-white rounded-full text-sm">
                  3
                </div>
                <h4 className="text-lg font-bold text-yellow-900">「選ばれる保育士」になろう</h4>
              </div>
              <p className="text-base text-gray-700 leading-relaxed mb-3">
                これからの時代、ただ資格があるだけでは生き残れません。
              </p>
              <div className="bg-white rounded-xl p-4 border-2 border-yellow-400 mb-3">
                <p className="text-base font-bold text-[var(--theme-600)] text-center">
                  「私だったら、私を指名する」。
                </p>
              </div>
              <p className="text-base text-gray-700 leading-relaxed mb-3">
                胸を張ってそう言える自分であることが、最強の顧客目線であり、あなた自身の価値（ブランド）になります。
              </p>
              <div className="bg-[var(--theme-100)] rounded-lg p-4 border-l-4 border-[var(--theme-500)]">
                <p className="text-base font-bold text-[var(--theme-800)] text-center">
                  今日一日、未来の我が子に見せられる<br />「プロの仕事」をしてください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* フォームセクション（最後：連絡先と送信） */}
      {stepKey === 'submit' && (
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-t-4 border-[var(--theme-500)]">
          <div className="text-center mb-10">
            <div className="w-16 h-16 flex items-center justify-center bg-[var(--theme-500)] text-white rounded-full mx-auto mb-4">
              <i className="ri-mail-send-line text-3xl"></i>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mb-3">最後に、連絡先を</h3>
            <p className="text-lg text-gray-600">
              ここまでの内容をご確認のうえ、送信してください
            </p>
          </div>

          {/* ここまでの回答のまとめ。各行から該当の画面へ戻れる */}
          <div className="mb-8 bg-gray-50 border border-gray-200 rounded-xl divide-y divide-gray-200">
            {(
              [
                { label: 'お名前', value: formData.お名前, to: 'profile' as StepKey },
                { label: '保有資格', value: selectedQualifications.join('・'), to: 'profile' as StepKey },
                {
                  label: '資格証',
                  value: isAlreadyUploaded ? 'タイミーにアップロード済み' : `写真を提出（${licenseFiles.length}枚）`,
                  to: 'profile' as StepKey,
                },
                {
                  label: '通勤手段',
                  value: formData.通勤手段 === '徒歩' ? '公共交通機関と徒歩' : formData.通勤手段,
                  to: 'commute' as StepKey,
                },
                { label: '持ち物・服装', value: itemsConfirmed ? '確認済み' : '未確認', to: 'items' as StepKey },
                { label: 'マニュアル', value: manualDone ? '最後まで確認済み' : '未読', to: 'manual' as StepKey },
              ] as const
            ).map((row) => (
              <div key={row.label} className="flex items-center gap-3 px-4 py-3">
                <span className="w-24 flex-shrink-0 text-sm text-gray-500">{row.label}</span>
                <span className="flex-1 text-base font-medium text-gray-800 break-words">{row.value || '—'}</span>
                <button
                  type="button"
                  onClick={() => jumpTo(row.to)}
                  className="text-sm text-[var(--theme-600)] underline underline-offset-2 whitespace-nowrap cursor-pointer"
                >
                  変更
                </button>
              </div>
            ))}
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-6"
           
            id="worker-confirmation-form"
          >
            <div>
              <label htmlFor="email" className="block text-base font-semibold text-gray-700 mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                inputMode="email"
                className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[var(--theme-500)] focus:border-transparent text-lg"
                placeholder="example@email.com"
              />
              <p className="text-sm text-gray-500 mt-2">園からのご連絡はこのアドレスに届きます</p>
            </div>

            <div>
              <label
                htmlFor="その他ご質問ご連絡事項"
                className="block text-base font-semibold text-gray-700 mb-2"
              >
                その他ご質問・ご連絡事項
              </label>
              <textarea
                id="その他ご質問ご連絡事項"
                name="その他ご質問ご連絡事項"
                value={formData.その他ご質問ご連絡事項}
                onChange={handleChange}
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-500)] focus:border-transparent text-base resize-none"
                placeholder="ご質問やご連絡事項がございましたらご記入ください（500文字以内）"
              />
              <p className="text-sm text-gray-500 mt-2">{formData.その他ご質問ご連絡事項.length}/500文字</p>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                <p className="text-green-800 font-medium">送信が完了しました。ありがとうございます。</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-error-warning-fill text-2xl text-red-500"></i>
                <p className="text-red-800 font-medium">送信に失敗しました。もう一度お試しください。</p>
              </div>
            )}

            <p className="text-sm text-gray-500 text-center mt-2">
              送信することで
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--theme-500)] hover:text-[var(--theme-700)] underline underline-offset-2 mx-1 cursor-pointer"
              >
                プライバシーポリシー
              </a>
              に同意したものとみなします。
            </p>
          </form>

          {/* 送ったあとも困らないように、あおいへの入口をここに置く。
              🟥 質問ボタンは園が承認した「よくある質問」から（lib/faq.ts）。押すとそのままあおいが答える */}
          <div className="mt-8 border-t border-gray-100 pt-6">
            <FaqEntry garden={GARDEN_SLUG} />
          </div>
        </div>
      </section>
      )}

      {/* ウィザードの足元ナビ（前へ／次へ）。最後の画面は送信ボタンが主役なので「前へ」だけ */}
      <div
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 px-4 pt-3"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          {stepIndex > 0 && (
            <button
              type="button"
              onClick={goBack}
              className="h-14 px-5 rounded-xl border-2 border-gray-200 bg-white text-gray-700 font-bold text-base whitespace-nowrap cursor-pointer active:scale-[0.98] transition-transform"
            >
              <span className="flex items-center gap-1">
                <i className="ri-arrow-left-line text-xl"></i>
                前へ
              </span>
            </button>
          )}
          {stepKey !== 'submit' ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!gate.ok}
              className="flex-1 h-14 rounded-xl bg-[var(--theme-500)] text-white font-bold text-lg shadow-lg hover:bg-[var(--theme-600)] disabled:bg-gray-300 disabled:shadow-none disabled:cursor-not-allowed cursor-pointer active:scale-[0.98] transition-transform whitespace-nowrap"
            >
              <span className="flex items-center justify-center gap-2">
                {stepKey === 'intro' ? 'はじめる' : '次へ'}
                <i className="ri-arrow-right-line text-xl"></i>
              </span>
            </button>
          ) : (
            <button
              type="submit"
              form="worker-confirmation-form"
              disabled={isSubmitting}
              className="flex-1 h-14 rounded-xl bg-[var(--theme-500)] text-white font-bold text-lg shadow-lg hover:bg-[var(--theme-600)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-[0.98] transition-transform whitespace-nowrap"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  送信中...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-send-plane-fill"></i>
                  送信する
                </span>
              )}
            </button>
          )}
        </div>
        {!gate.ok && gate.hint && (
          <p className="max-w-5xl mx-auto text-sm text-gray-500 mt-2 text-center">{gate.hint}</p>
        )}
        {stepKey !== 'submit' && !reachedBottom && (
          <button
            type="button"
            onClick={() => window.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' })}
            aria-label="下へ読み進む"
            className="absolute -top-16 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center text-[var(--theme-600)] animate-bounce cursor-pointer"
          >
            <i className="ri-arrow-down-line text-2xl"></i>
          </button>
        )}
      </div>
    </div>
  );
}
